import {
  Alert,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useValidateGangName } from '../../../hooks/useGangHooks';
import { GangCreationFormData } from '../../../types/gangCreationTypes';
import { StepNavigation } from '../../molecules/StepNavigation';
import { GangLifecycleMode } from '../../../stores/gangLifecycleStore';

const isValidGangNameForValidation = (name: string): boolean => {
  const trimmed = name?.trim() || '';
  return trimmed.length >= 2;
};

export interface GangIdentityStepProps {
  form: UseFormReturn<GangCreationFormData>;
  mode: GangLifecycleMode;
  originalName?: string | null;
  onNext: () => void;
  disableNext?: boolean;
  onSave?: () => void;
  saveDisabled?: boolean;
  showSave?: boolean;
}

export const GangIdentityStep = ({
  form,
  mode,
  originalName = null,
  onNext,
  disableNext = false,
  onSave,
  saveDisabled = false,
  showSave = true,
}: GangIdentityStepProps) => {
  const trimmedName = form.watch('name')?.trim() ?? '';
  const trimmedOriginalName = (originalName ?? '').trim();
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    if (!hasInteracted && trimmedName.length > 0) {
      setHasInteracted(true);
    }
  }, [trimmedName, hasInteracted]);

  const hasEditedName = trimmedName !== trimmedOriginalName;

  const shouldValidate =
    mode === 'create'
      ? isValidGangNameForValidation(trimmedName)
      : hasEditedName && isValidGangNameForValidation(trimmedName);

  const {
    data: validationResult,
    isLoading: isValidating,
    error: validationError,
  } = useValidateGangName(trimmedName, {
    enabled: shouldValidate,
  });

  const fieldState = form.getFieldState('name');
  const isFormValid = !fieldState.error && trimmedName.length >= 2;
  const isServerValid =
    mode === 'edit' && !hasEditedName
      ? true
      : (validationResult?.isValid ?? false);

  const canNavigate =
    !disableNext && isFormValid && (mode === 'edit' ? true : isServerValid);

  const canSave = canNavigate && hasEditedName && isServerValid;

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {mode === 'create'
          ? 'Choose a unique name for your gang. This will be used across the platform.'
          : 'Update the gang name. Existing references will reflect the new name immediately.'}
      </Typography>

      <Controller
        name="name"
        control={form.control}
        render={({ field, fieldState: controllerState }) => {
          const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
            if (!hasInteracted) {
              setHasInteracted(true);
            }
            field.onChange(event);
          };

          return (
            <TextField
              {...field}
              onChange={handleChange}
              label="Gang Name"
              data-testid="gang-name-input"
              inputProps={{
                'data-testid': 'gang-name-input-field',
              }}
              fullWidth
              variant="outlined"
              error={
                !!controllerState.error ||
                (shouldValidate &&
                  validationResult &&
                  !validationResult.isValid)
              }
              helperText={
                controllerState.error?.message ||
                (shouldValidate && validationResult && !validationResult.isValid
                  ? validationResult.message
                  : '') ||
                (validationError ? 'Failed to validate gang name' : '')
              }
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: isValidating ? (
                  <CircularProgress size={20} />
                ) : undefined,
              }}
            />
          );
        }}
      />

      {validationResult?.isValid &&
        hasInteracted &&
        (mode === 'create' || hasEditedName) && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            data-testid="gang-identity-success-alert"
          >
            Gang name is available!
          </Alert>
        )}

      {mode === 'edit' && !hasEditedName && hasInteracted && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          data-testid="gang-identity-info-alert"
        >
          Gang name is unchanged. Update the name to trigger validation if
          needed.
        </Alert>
      )}

      {shouldValidate &&
        !validationResult?.isValid &&
        !isValidating &&
        (validationResult || validationError) && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            data-testid="gang-identity-error-alert"
          >
            {validationResult?.message ||
              validationError?.message ||
              'This gang name is not available'}
          </Alert>
        )}

      <StepNavigation
        onNext={onNext}
        nextLabel="Next: Select Leaders"
        disableNext={!canNavigate}
        onSecondary={mode === 'edit' ? onSave : undefined}
        secondaryLabel={mode === 'edit' ? 'Save' : undefined}
        secondaryDisabled={saveDisabled || !canSave || !showSave}
      />
    </Box>
  );
};
